import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { categorySurface, categoryTheme } from './categoryThemes';

interface CategoryHeroProps {
  categoryId: string;
  label: string;
  testCount?: number;
  subjectCount: number;
  onBack: () => void;
}

/** The head of a category's own page: its accent, a watermark icon and the way back to every category. */
export function CategoryHero({ categoryId, label, testCount, subjectCount, onBack }: CategoryHeroProps) {
  const { t } = useTranslation();
  const { accent, icon: Icon } = categoryTheme(categoryId);

  return (
    <section
      aria-labelledby="category-hero-title"
      style={categorySurface(accent)}
      className="relative overflow-hidden rounded-3xl border px-5 py-5 md:px-8 md:py-7"
    >
      <Icon
        aria-hidden="true"
        strokeWidth={1.1}
        className="pointer-events-none absolute -right-8 -top-10 h-44 w-44 opacity-[0.13] md:-right-10 md:-top-12 md:h-64 md:w-64"
        style={{ color: accent }}
      />
      <button
        type="button"
        onClick={onBack}
        className="relative -my-2.5 inline-flex min-h-11 items-center gap-1.5 rounded-md pr-3 text-[13px] font-semibold opacity-80 lg:my-0 lg:min-h-0 lg:pr-0 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ArrowLeft aria-hidden="true" size={15} />
        {t('portal_category_back')}
      </button>
      <h2
        id="category-hero-title"
        className="relative mt-3 font-display text-[28px] font-extrabold leading-[1.1] tracking-[-0.04em] md:text-[40px]"
      >
        {label}
      </h2>
      <p className="relative mt-2 text-[13.5px] font-medium opacity-85 md:text-[14.5px]">
        {testCount !== undefined && (
          <>
            <span className="font-semibold tabular-nums">{testCount}</span> {t('pager_noun_tests')}
          </>
        )}
        {testCount !== undefined && subjectCount > 0 && ' · '}
        {subjectCount > 0 && t('portal_category_subjects', { count: subjectCount })}
      </p>
    </section>
  );
}

export default CategoryHero;
